import { useTranslation } from "react-i18next";
import { z } from "zod";

import { ClientTypeEnum, PaymentTypeEnum } from "@/types/client/ClientType";

export const useClientSchema = () => {
  const { t } = useTranslation();

  const clientInfo = z.object({
    name: z.string()
      .nonempty({ message: t("errors.requiredField", { fieldName: t("common.name") }) })
      .min(2, { message: t("errors.fieldMin", { fieldName: t("common.name") }) }).max(32, { message: t("errors.fieldMax", { fieldName: t("common.name") }) }),
    firstName: z
      .string()
      .nonempty({ message: t("errors.requiredField", { fieldName: t("common.firstName") }) })
      .min(2, { message: t("errors.fieldMin", { fieldName: t("common.firstName") }) }).max(32, { message: t("errors.fieldMax", { fieldName: t("common.firstName") }) }),
    lastName: z
      .string()
      .nonempty({ message: t("errors.requiredField", { fieldName: t("common.lastName") }) })
      .min(2, { message: t("errors.fieldMin", { fieldName: t("common.lastName") }) }).max(32, { message: t("errors.fieldMax", { fieldName: t("common.lastName") }) }),
    infix: z
      .string()
      .optional()
      .refine(
        (value) => !value || (value.length >= 2 && value.length <= 32),
        { message: t("errors.fieldMinMax", { fieldName: t("common.infix") }) }
      ),
    emailAddress: z
      .string()
      .nonempty({ message: t("errors.requiredField", { fieldName: t("common.email") }) })
      .email({ message: t("errors.emailInvalid", { fieldName: t("common.email") }) })
      .refine((value) => value.length <= 256, {
        message: t("errors.emailMax")
      }),
    phoneNumber: z.string(),
    jobTitle: z
      .string()
      .refine(
        (value) => !value || (value.length >= 2 && value.length <= 32),
        { message: t("errors.fieldMinMax", { fieldName: t("admin.client.addClient.clientInfo.jobTitle") }) }
      ),
  });

  const clientLogo = z.object({
    imagePath: z.string()
      .refine(
        (value) => !value || /^(https?:\/\/[^\s$.?#].[^\s]*)$/.test(value),
        { message: t("errors.fieldValid", { fieldName: t("admin.client.addClient.clientSettings.logoUrl") }) }
      )
      .refine(
        (value) =>
          !value ||
          /\.(png|jpg|jpeg|svg|gif|bmp|webp)$/i.test(value),
        { message: t("admin.client.addClient.clientSettings.logoNotice") }
      ),
  });

  const atsValidationSchema = z.object({
    clientType: z.literal(ClientTypeEnum.ATS),
    // ATS credentials are no longer required - users must configure via integrations
    recruitNowApiKey: z.string().optional(),
    recruitNowUrl: z.string().optional(),
  });

  const feedValidationSchema = z.object({
    clientType: z.literal(ClientTypeEnum.FEED),
    feedUrl: z
      .string()
      .min(1, { message: t("errors.requiredField", { fieldName: t("common.feed") }) })
      .url({ message: t("errors.validUrl") }),
  });

  const requireFeedUrlWhenFeed = (
    values: { clientType: ClientTypeEnum; feedUrl?: string },
    ctx: z.RefinementCtx
  ) => {
    if (values.clientType !== ClientTypeEnum.FEED) {
      return;
    }

    const feedUrl = feedValidationSchema.shape.feedUrl.safeParse(
      values.feedUrl ?? ""
    );
    if (feedUrl.success) {
      return;
    }

    feedUrl.error.issues.forEach((issue) => {
      ctx.addIssue({
        code: "custom",
        path: ["feedUrl"],
        message: issue.message,
      });
    });
  };

  const combinedValidationSchema = z
    .object({
      clientType: z.enum(ClientTypeEnum),
      recruitNowApiKey: z.string().optional(),
      recruitNowUrl: z.string().optional(),
      feedUrl: z.string().optional(),
      feedFormat: z.string().optional(),
    })
    .superRefine(requireFeedUrlWhenFeed);

  const addClientFormShape = {
    clientType: z.enum(ClientTypeEnum),
    emailAddress: z.string(),
    feedFormat: z.string(),
    feedUrl: z.string(),
    firstName: z.string(),
    paymentType: z.enum(PaymentTypeEnum),
    imagePath: z.string(),
    infix: z.string().optional(),
    isSelfService: z.boolean().optional(),
    jobTitle: z.string(),
    lastName: z.string(),
    marginIncluded: z.boolean(),
    name: z.string(),
    phoneNumber: z.string(),
    status: z.boolean(),
  };

  const clientInfoStep = z.object({
    ...addClientFormShape,
    ...clientInfo.shape,
  });

  const clientLogoStep = z.object({
    ...addClientFormShape,
    ...clientLogo.shape,
  });

  const clientTypeStep = z
    .object(addClientFormShape)
    .superRefine(requireFeedUrlWhenFeed);

  return {
    clientInfo,
    clientLogo,
    atsValidationSchema,
    feedValidationSchema,
    combinedValidationSchema,
    clientInfoStep,
    clientLogoStep,
    clientTypeStep,
  };
};
